import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, ArrowLeft, ArrowRight, CheckCircle2, Sparkles,
  Wrench, Layers, Zap, Shield, Eye, Settings2, Printer,
} from "lucide-react";
import { getQuestions, recommend } from "@/lib/verfahren-finder-rules";
import { verfahren as verfahrenData } from "@/lib/verfahren-data";
import { useT, useI18n } from "@/lib/i18n";
import { verfahrenLabels } from "@/lib/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  Stahl: Layers, Aluminium: Layers, Messing: Layers, Kupfer: Layers,
  Edelstahl: Layers, Anderes: Settings2,
  Korrosionsschutz: Shield, "Bessere Optik": Eye, Leitfähigkeit: Zap,
  Verschleißschutz: Wrench, "Technischer Spezialzweck": Sparkles,
};

const initialAnswers = { material: "", goal: "", quantity: "", timing: "" };

const COPY = {
  de: {
    badge_new: "NEU",
    intro: "Vier kurze Fragen genügen — und Sie erhalten eine fundierte Empfehlung aus unseren elf Verfahren plus den direkten Draht zum Sekretariat in Mels.",
    your_recommendation: "Ihre Empfehlung",
    question_of: (a, b) => `Frage ${a} von ${b}`,
    step: "Schritt",
    answered: "beantwortet",
    auto_advance: "Antworten Sie zur Auswahl — wir wechseln automatisch.",
    back: "Zurück",
    sidebar_disclaimer: "Die Empfehlung basiert auf jahrzehntelanger Galvanik-Erfahrung der Fritz Kamm AG und ersetzt keine verbindliche Offerte.",
    overline_recommendation: "— Empfehlung",
    also_suitable: "Auch geeignet",
    label_material: "Material", label_goal: "Ziel", label_quantity: "Stückzahl", label_timing: "Termin",
    direct_line: "— Direkter Draht ans Sekretariat",
    name: "Name", email: "E-Mail", company: "Firma", phone: "Telefon",
    name_ph: "Max Muster", email_ph: "max@firma.ch", company_ph: "Muster AG", phone_ph: "+41 ...",
    adjust: "Antworten anpassen",
    save_pdf: "Als PDF speichern",
    submit: "Empfehlung anfragen",
    sending: "Wird gesendet…",
    success_title: "Anfrage gesendet.",
    success_body_1: "Vielen Dank. Ihre Empfehlung —",
    success_body_2: "— liegt nun bei unserem Sekretariat in Mels. Wir melden uns innerhalb eines Werktags persönlich bei Ihnen.",
    restart: "Neue Empfehlung starten",
    err_required: "Bitte Name und E-Mail ausfüllen.",
    err_send: "Übermittlung fehlgeschlagen.",
    ok_sent: "Anfrage gesendet. Wir melden uns innerhalb eines Werktags.",
    pdf_pop_blocked: "Bitte Pop-up-Blocker für diese Seite deaktivieren.",
    pdf_msg: (rec, ans) => `Verfahren-Finder Ergebnis:
• Material: ${ans.material}
• Ziel: ${ans.goal}
• Stückzahl: ${ans.quantity}
• Termin: ${ans.timing}
• Empfehlung: ${rec.primary}${rec.secondary.length ? " · " + rec.secondary.join(", ") : ""}

Bitte um Rückmeldung zu Machbarkeit und Offerte.`,
  },
  en: {
    badge_new: "NEW",
    intro: "Four short questions — and you'll receive a well-founded recommendation from our eleven processes, plus a direct line to our office in Mels.",
    your_recommendation: "Your recommendation",
    question_of: (a, b) => `Question ${a} of ${b}`,
    step: "Step",
    answered: "answered",
    auto_advance: "Pick an option — we'll advance automatically.",
    back: "Back",
    sidebar_disclaimer: "This recommendation draws on decades of electroplating experience at Fritz Kamm AG and does not replace a binding quote.",
    overline_recommendation: "— Recommendation",
    also_suitable: "Also suitable",
    label_material: "Material", label_goal: "Goal", label_quantity: "Quantity", label_timing: "Timing",
    direct_line: "— Direct line to our office",
    name: "Name", email: "Email", company: "Company", phone: "Phone",
    name_ph: "Jane Doe", email_ph: "jane@company.com", company_ph: "Acme Ltd.", phone_ph: "+41 ...",
    adjust: "Adjust answers",
    save_pdf: "Save as PDF",
    submit: "Request recommendation",
    sending: "Sending…",
    success_title: "Request sent.",
    success_body_1: "Thank you. Your recommendation —",
    success_body_2: "— is now with our office in Mels. We'll respond personally within one business day.",
    restart: "Start a new recommendation",
    err_required: "Please fill in name and email.",
    err_send: "Submission failed.",
    ok_sent: "Request sent. We'll respond within one business day.",
    pdf_pop_blocked: "Please disable your pop-up blocker for this page.",
    pdf_msg: (rec, ans) => `Process Finder result:
• Material: ${ans.material}
• Goal: ${ans.goal}
• Quantity: ${ans.quantity}
• Timing: ${ans.timing}
• Recommendation: ${rec.primary}${rec.secondary.length ? " · " + rec.secondary.join(", ") : ""}

Please reply regarding feasibility and quote.`,
  },
  fr: {
    badge_new: "NOUVEAU",
    intro: "Quatre questions courtes — et vous recevez une recommandation solide parmi nos onze procédés, plus un contact direct avec notre bureau de Mels.",
    your_recommendation: "Votre recommandation",
    question_of: (a, b) => `Question ${a} sur ${b}`,
    step: "Étape",
    answered: "répondu",
    auto_advance: "Choisissez une option — nous passons à la suite automatiquement.",
    back: "Retour",
    sidebar_disclaimer: "Cette recommandation s'appuie sur des décennies d'expérience galvanique chez Fritz Kamm SA et ne remplace pas un devis ferme.",
    overline_recommendation: "— Recommandation",
    also_suitable: "Convient aussi",
    label_material: "Matériau", label_goal: "Objectif", label_quantity: "Quantité", label_timing: "Délai",
    direct_line: "— Contact direct avec notre bureau",
    name: "Nom", email: "E-mail", company: "Société", phone: "Téléphone",
    name_ph: "Jean Dupont", email_ph: "jean@societe.ch", company_ph: "Société SA", phone_ph: "+41 ...",
    adjust: "Modifier les réponses",
    save_pdf: "Enregistrer en PDF",
    submit: "Demander la recommandation",
    sending: "Envoi en cours…",
    success_title: "Demande envoyée.",
    success_body_1: "Merci. Votre recommandation —",
    success_body_2: "— est maintenant chez notre bureau à Mels. Nous vous répondrons personnellement sous un jour ouvré.",
    restart: "Démarrer une nouvelle recommandation",
    err_required: "Veuillez renseigner nom et e-mail.",
    err_send: "L'envoi a échoué.",
    ok_sent: "Demande envoyée. Nous vous répondons sous un jour ouvré.",
    pdf_pop_blocked: "Veuillez désactiver le bloqueur de pop-ups pour cette page.",
    pdf_msg: (rec, ans) => `Résultat du sélecteur de procédé :
• Matériau : ${ans.material}
• Objectif : ${ans.goal}
• Quantité : ${ans.quantity}
• Délai : ${ans.timing}
• Recommandation : ${rec.primary}${rec.secondary.length ? " · " + rec.secondary.join(", ") : ""}

Merci de me confirmer la faisabilité et le devis.`,
  },
  it: {
    badge_new: "NUOVO",
    intro: "Quattro domande brevi — e ricevete una raccomandazione fondata tra i nostri undici procedimenti, oltre al contatto diretto con la nostra segreteria a Mels.",
    your_recommendation: "La Sua raccomandazione",
    question_of: (a, b) => `Domanda ${a} di ${b}`,
    step: "Fase",
    answered: "risposto",
    auto_advance: "Scegli un'opzione — passiamo avanti in automatico.",
    back: "Indietro",
    sidebar_disclaimer: "La raccomandazione si basa su decenni di esperienza galvanica di Fritz Kamm SA e non sostituisce un'offerta vincolante.",
    overline_recommendation: "— Raccomandazione",
    also_suitable: "Adatto anche",
    label_material: "Materiale", label_goal: "Obiettivo", label_quantity: "Quantità", label_timing: "Tempistica",
    direct_line: "— Filo diretto con la segreteria",
    name: "Nome", email: "E-mail", company: "Azienda", phone: "Telefono",
    name_ph: "Mario Rossi", email_ph: "mario@azienda.ch", company_ph: "Azienda SA", phone_ph: "+41 ...",
    adjust: "Modifica le risposte",
    save_pdf: "Salva in PDF",
    submit: "Richiedi la raccomandazione",
    sending: "Invio in corso…",
    success_title: "Richiesta inviata.",
    success_body_1: "Grazie. La Sua raccomandazione —",
    success_body_2: "— è ora alla nostra segreteria a Mels. La contatteremo personalmente entro un giorno lavorativo.",
    restart: "Avvia una nuova raccomandazione",
    err_required: "Compili nome ed e-mail.",
    err_send: "Invio non riuscito.",
    ok_sent: "Richiesta inviata. Risponderemo entro un giorno lavorativo.",
    pdf_pop_blocked: "Disattivi il blocco pop-up per questa pagina.",
    pdf_msg: (rec, ans) => `Risultato del Selettore Procedimento:
• Materiale: ${ans.material}
• Obiettivo: ${ans.goal}
• Quantità: ${ans.quantity}
• Tempistica: ${ans.timing}
• Raccomandazione: ${rec.primary}${rec.secondary.length ? " · " + rec.secondary.join(", ") : ""}

Vi prego di confermare fattibilità e offerta.`,
  },
};

export const VerfahrenFinder = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const labelMap = verfahrenLabels[lang] || verfahrenLabels.de;
  const trVerfahren = (v) => labelMap[v] || v;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const [showResult, setShowResult] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const questions = useMemo(() => getQuestions(lang), [lang]);
  const total = questions.length;
  const current = questions[step];
  const recommendation = showResult ? recommend(answers, lang) : null;

  // For showing answer label (DE option v stored, but display localized l)
  const optionLabel = (qid, v) => {
    const q = questions.find((x) => x.id === qid);
    const opt = q?.options.find((o) => o.v === v);
    return opt?.l || v;
  };

  const select = (value) => {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    setTimeout(() => {
      if (step < total - 1) setStep(step + 1);
      else setShowResult(true);
    }, 220);
  };

  const back = () => {
    if (showResult) { setShowResult(false); return; }
    if (step > 0) setStep(step - 1);
  };

  const restart = () => {
    setAnswers(initialAnswers);
    setStep(0); setShowResult(false); setSuccess(false);
    setForm({ name: "", email: "", company: "", phone: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error(c.err_required); return; }
    setSubmitting(true);
    const message = c.pdf_msg(recommendation, answers);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.company) fd.append("company", form.company);
      if (form.phone) fd.append("phone", form.phone);
      fd.append("message", message);
      fd.append("service_interest", recommendation.primary);
      fd.append("material", answers.material);
      fd.append("goal", answers.goal);
      fd.append("quantity", answers.quantity);
      fd.append("timing", answers.timing);
      fd.append("source", "verfahren_finder");
      await axios.post(`${API}/consultation`, fd);
      setSuccess(true);
      toast.success(c.ok_sent);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        c.err_send;
      toast.error(typeof detail === "string" ? detail : c.err_send);
    } finally {
      setSubmitting(false);
    }
  };

  const progress = showResult ? 100 : ((step + 1) / total) * 100;
  const completed = Object.values(answers).filter(Boolean).length;

  const printPDF = () => {
    if (!recommendation) return;
    const tech = verfahrenData[recommendation.primary] || null;
    const localeForDate = lang === "en" ? "en-GB" : lang === "fr" ? "fr-CH" : lang === "it" ? "it-CH" : "de-CH";
    const date = new Date().toLocaleDateString(localeForDate, { day: "2-digit", month: "long", year: "numeric" });
    const escape = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const labels = lang === "en"
      ? { recommendation: "Process recommendation", recommended: "Recommended by the Process Finder", recommendation_o: "Recommendation",
          your_data: "Your inputs", tech: "Technical key data ·", also: "Also suitable",
          material: "Material", goal: "Goal", quantity: "Quantity", timing: "Timing",
          schicht: "Typical thickness", korr: "Corrosion protection", mat: "Suitable materials",
          opt: "Appearance", serien: "Series-capable", anw: "Typical applications",
          contact: "Contact", disclaimer: "This recommendation is based on the Process Finder inputs and does not replace a binding quote." }
      : lang === "fr"
      ? { recommendation: "Recommandation de procédé", recommended: "Recommandé par le sélecteur de procédé", recommendation_o: "Recommandation",
          your_data: "Vos données", tech: "Caractéristiques techniques ·", also: "Convient aussi",
          material: "Matériau", goal: "Objectif", quantity: "Quantité", timing: "Délai",
          schicht: "Épaisseur typique", korr: "Protection anti-corrosion", mat: "Matériaux compatibles",
          opt: "Aspect", serien: "Aptitude série", anw: "Applications typiques",
          contact: "Contact", disclaimer: "Cette recommandation est basée sur les indications du sélecteur de procédé et ne remplace pas un devis ferme." }
      : lang === "it"
      ? { recommendation: "Raccomandazione procedimento", recommended: "Consigliato dal Selettore Procedimento", recommendation_o: "Raccomandazione",
          your_data: "I Suoi dati", tech: "Caratteristiche tecniche ·", also: "Adatto anche",
          material: "Materiale", goal: "Obiettivo", quantity: "Quantità", timing: "Tempistica",
          schicht: "Spessore tipico", korr: "Protezione anticorrosiva", mat: "Materiali idonei",
          opt: "Aspetto", serien: "Idoneità in serie", anw: "Applicazioni tipiche",
          contact: "Contatti", disclaimer: "Questa raccomandazione si basa sulle indicazioni del Selettore Procedimento e non sostituisce un'offerta vincolante." }
      : { recommendation: "Verfahren-Empfehlung", recommended: "Empfohlen vom Verfahren-Finder", recommendation_o: "Empfehlung",
          your_data: "Ihre Angaben", tech: "Technische Kennwerte ·", also: "Auch geeignet",
          material: "Material", goal: "Ziel", quantity: "Stückzahl", timing: "Termin",
          schicht: "Typische Schichtdicke", korr: "Korrosionsschutz", mat: "Geeignete Werkstoffe",
          opt: "Optik", serien: "Serientauglich", anw: "Typische Anwendungen",
          contact: "Kontakt", disclaimer: "Diese Empfehlung basiert auf den Angaben des Verfahren-Finders und ersetzt keine verbindliche Offerte." };

    const techRows = tech ? `
        <tr><td>${labels.schicht}</td><td>${escape(tech.schichtdicke)}</td></tr>
        <tr><td>${labels.korr}</td><td>${escape(tech.korrosionsschutz)}</td></tr>
        <tr><td>${labels.mat}</td><td>${tech.materialien.map(escape).join(", ")}</td></tr>
        <tr><td>${labels.opt}</td><td>${escape(tech.optik)}</td></tr>
        <tr><td>${labels.serien}</td><td>${escape(tech.serientauglich)}</td></tr>
        <tr><td>${labels.anw}</td><td>${tech.anwendungen.map(escape).join(", ")}</td></tr>` : "";

    const html = `<!DOCTYPE html>
<html lang="${lang}"><head><meta charset="utf-8"><title>${labels.recommendation} · Fritz Kamm AG</title>
<style>
  @page { size: A4; margin: 18mm 16mm; } * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; margin: 0; }
  .head { display:flex; align-items:flex-start; justify-content:space-between; border-bottom:2px solid #111; padding-bottom:14px; margin-bottom:28px; }
  .brand { display:flex; align-items:center; gap:12px; }
  .brand-mark { width:38px; height:38px; border:1.5px solid #111; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px; letter-spacing:-.02em; }
  .brand-name { font-weight:700; font-size:14px; letter-spacing:.04em; }
  .brand-sub { font-size:10px; letter-spacing:.22em; text-transform:uppercase; color:#666; margin-top:2px; }
  .meta { font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:#666; text-align:right; line-height:1.6; }
  .hero { margin-bottom:30px; }
  .overline { font-size:10px; letter-spacing:.22em; text-transform:uppercase; color:#666; margin-bottom:8px; }
  h1 { font-size:38px; line-height:1.05; letter-spacing:-.03em; margin:0 0 6px 0; font-weight:700; }
  .rule { width:60px; height:2px; background:#111; margin:12px 0 18px; }
  .lead { font-size:13px; line-height:1.65; color:#333; max-width:520px; }
  .answers, .specs { width:100%; border-collapse:collapse; margin:24px 0 28px; }
  .answers td { padding:10px 12px; font-size:12px; border:1px solid #ddd; vertical-align:top; }
  .answers td:first-child { width:32%; color:#666; text-transform:uppercase; letter-spacing:.14em; font-size:10px; background:#fafafa; }
  .answers td:last-child { color:#111; font-weight:600; }
  .specs td { padding:9px 12px; font-size:12px; border-bottom:1px solid #eee; vertical-align:top; }
  .specs td:first-child { width:38%; color:#666; }
  .pill { display:inline-block; padding:4px 10px; border:1px solid #ddd; font-size:11px; margin:0 4px 4px 0; }
  .foot { border-top:1px solid #ddd; padding-top:16px; margin-top:32px; display:flex; justify-content:space-between; gap:16px; font-size:11px; color:#555; line-height:1.6; }
  .foot strong { color:#111; }
  h2 { font-size:16px; letter-spacing:-.01em; margin:0 0 10px; font-weight:700; }
  .badge { display:inline-block; background:#111; color:#fff; padding:4px 10px; font-size:10px; letter-spacing:.18em; text-transform:uppercase; margin-bottom:14px; }
</style></head><body>
  <div class="head">
    <div class="brand">
      <div class="brand-mark">FK</div>
      <div>
        <div class="brand-name">FRITZ KAMM AG</div>
        <div class="brand-sub">${lang === "en" ? "Electroplating · Mels · Switzerland" : lang === "fr" ? "Galvanoplastie · Mels · Suisse" : lang === "it" ? "Galvanica · Mels · Svizzera" : "Galvanik · Mels · Schweiz"}</div>
      </div>
    </div>
    <div class="meta">${labels.recommendation}<br>${escape(date)}<br>ISO 9001 · ISO 14001</div>
  </div>

  <div class="hero">
    <div class="badge">${labels.recommended}</div>
    <div class="overline">${labels.recommendation_o}</div>
    <h1>${escape(trVerfahren(recommendation.primary))}</h1>
    <div class="rule"></div>
    <div class="lead">${escape(recommendation.rationale)}</div>
  </div>

  <h2>${labels.your_data}</h2>
  <table class="answers">
    <tr><td>${labels.material}</td><td>${escape(optionLabel("material", answers.material))}</td></tr>
    <tr><td>${labels.goal}</td><td>${escape(optionLabel("goal", answers.goal))}</td></tr>
    <tr><td>${labels.quantity}</td><td>${escape(optionLabel("quantity", answers.quantity))}</td></tr>
    <tr><td>${labels.timing}</td><td>${escape(optionLabel("timing", answers.timing))}</td></tr>
  </table>

  ${tech ? `<h2>${labels.tech} ${escape(trVerfahren(recommendation.primary))}</h2>
  <table class="specs">${techRows}</table>` : ""}

  ${recommendation.secondary?.length ? `<div class="also"><h2>${labels.also}</h2>${recommendation.secondary.map((s) => `<span class="pill">${escape(trVerfahren(s))}</span>`).join("")}</div>` : ""}

  <div class="foot">
    <div><strong>Fritz Kamm AG</strong><br>Zeughausstrasse 12<br>CH-8887 Mels</div>
    <div><strong>${labels.contact}</strong><br>+41 81 723 16 42<br>sekretariat@f-kamm.ch</div>
    <div style="text-align:right;max-width:240px;">${labels.disclaimer}</div>
  </div>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { toast.error(c.pdf_pop_blocked); return; }
    w.document.open(); w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (_) { /* ignore */ } }, 350);
  };

  return (
    <section
      id="finder"
      data-testid="finder-section"
      className="relative bg-[#0E0E10] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 -left-40 w-[400px] h-[400px] rounded-full bg-white/[0.03] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[400px] h-[400px] rounded-full bg-white/[0.03] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" />
                {c.badge_new}
              </span>
              <span>{t("sections.finder_overline", "— Verfahren-Finder")}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.finder_title_1", "Finden Sie in 30 Sekunden")}
              <br />
              <span className="text-zinc-500">{t("sections.finder_title_2", "Ihr passendes Verfahren.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        <div data-testid="finder-card" className="reveal relative bg-[#121214] border border-white/10 overflow-hidden">
          <div className="relative h-px bg-white/10">
            <div className="absolute top-0 left-0 h-px bg-white transition-all duration-500" style={{ width: `${progress}%` }} data-testid="finder-progress" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12">
            <aside className="lg:col-span-4 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0E0E10]">
              <div className="overline mb-6">
                {showResult ? c.your_recommendation : c.question_of(step + 1, total)}
              </div>

              <ol className="space-y-4">
                {questions.map((q, i) => {
                  const isDone = !!answers[q.id];
                  const isActive = !showResult && i === step;
                  return (
                    <li
                      key={q.id}
                      data-testid={`finder-step-${q.id}`}
                      className={`flex items-start gap-4 transition-opacity duration-300 ${isActive ? "opacity-100" : isDone ? "opacity-100" : "opacity-40"}`}
                    >
                      <div className={`flex-none w-7 h-7 border flex items-center justify-center text-[11px] font-medium transition-all duration-300 ${isDone ? "bg-white text-black border-white" : isActive ? "border-white text-white" : "border-white/20 text-zinc-500"}`}>
                        {isDone ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> : `0${i + 1}`}
                      </div>
                      <div className="pt-0.5 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                          {c.step} {i + 1}
                        </div>
                        <div className="text-sm text-white leading-snug">{q.label}</div>
                        {isDone && (
                          <div className="mt-1 text-[12px] text-zinc-400 truncate">
                            → {optionLabel(q.id, answers[q.id])}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-10 pt-6 border-t border-white/10 text-[12px] text-zinc-500 leading-relaxed">
                {c.sidebar_disclaimer}
              </div>
            </aside>

            <div className="lg:col-span-8 p-8 md:p-12 min-h-[520px] flex flex-col">
              {!showResult && (
                <div className="flex-1 flex flex-col">
                  <div className="overline mb-4">
                    {`0${step + 1}`} · {completed}/{total} {c.answered}
                  </div>
                  <h3 className="font-display text-2xl md:text-4xl font-semibold tracking-tight text-white leading-tight mb-10">
                    {current.label}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                    {current.options.map((opt) => {
                      const Icon = ICONS[opt.v];
                      const selected = answers[current.id] === opt.v;
                      const display = opt.l || opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => select(opt.v)}
                          data-testid={`finder-option-${current.id}-${opt.v.toLowerCase().replace(/ß/g, "ss").replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                          className={`group relative bg-[#121214] hover:bg-[#191A1D] text-left p-5 md:p-6 transition-all duration-300 ${selected ? "bg-white text-black hover:bg-white" : "text-white"}`}
                        >
                          <div className="flex items-center justify-between mb-4">
                            {Icon ? (
                              <Icon className={`w-5 h-5 ${selected ? "text-black" : "text-zinc-500 group-hover:text-white"} transition-colors`} strokeWidth={1.5} />
                            ) : (<span className="block w-5 h-5" />)}
                            <ArrowUpRight className={`w-4 h-4 transition-all duration-300 ${selected ? "text-black opacity-100 -translate-y-0.5 translate-x-0.5" : "text-zinc-500 opacity-0 group-hover:opacity-100"}`} strokeWidth={1.5} />
                          </div>
                          <div className="font-display text-lg md:text-xl tracking-tight">{display}</div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-auto pt-10 flex items-center justify-between">
                    <button type="button" onClick={back} disabled={step === 0} data-testid="finder-back" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                      <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                      {c.back}
                    </button>
                    <div className="text-[12px] text-zinc-500">{c.auto_advance}</div>
                  </div>
                </div>
              )}

              {showResult && !success && (
                <div data-testid="finder-result" className="flex-1 flex flex-col">
                  <div className="overline mb-4 text-zinc-300 result-rise" style={{ animationDelay: "0ms" }}>{c.overline_recommendation}</div>
                  <h3 className="font-display text-3xl md:text-5xl font-semibold tracking-tight text-white leading-[1] mb-2 result-rise" style={{ animationDelay: "120ms" }}>
                    {trVerfahren(recommendation.primary)}
                  </h3>
                  <div className="h-px w-24 bg-white/40 mb-6 result-line" />
                  <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-2xl mb-6 result-rise" style={{ animationDelay: "320ms" }}>
                    {recommendation.rationale}
                  </p>

                  {recommendation.secondary.length > 0 && (
                    <div className="mb-8 result-rise" style={{ animationDelay: "420ms" }}>
                      <div className="overline mb-3">{c.also_suitable}</div>
                      <div className="flex flex-wrap gap-2">
                        {recommendation.secondary.map((s) => (
                          <span key={s} className="inline-flex items-center px-3 py-1.5 border border-white/15 text-sm text-zinc-200">
                            {trVerfahren(s)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10 result-rise" style={{ animationDelay: "520ms" }}>
                    {[
                      [c.label_material, optionLabel("material", answers.material)],
                      [c.label_goal, optionLabel("goal", answers.goal)],
                      [c.label_quantity, optionLabel("quantity", answers.quantity)],
                      [c.label_timing, optionLabel("timing", answers.timing)],
                    ].map(([k, v]) => (
                      <div key={k} className="bg-[#121214] p-4 md:p-5">
                        <div className="overline mb-1.5">{k}</div>
                        <div className="text-sm text-white font-medium">{v}</div>
                      </div>
                    ))}
                  </div>

                  <form onSubmit={onSubmit} data-testid="finder-form" className="border-t border-white/10 pt-8 result-rise" style={{ animationDelay: "640ms" }}>
                    <div className="overline mb-4">{c.direct_line}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="overline block mb-2">{c.name} *</label>
                        <input data-testid="finder-form-name" className="field" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={c.name_ph} required />
                      </div>
                      <div>
                        <label className="overline block mb-2">{c.email} *</label>
                        <input data-testid="finder-form-email" type="email" className="field" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={c.email_ph} required />
                      </div>
                      <div>
                        <label className="overline block mb-2">{c.company}</label>
                        <input data-testid="finder-form-company" className="field" name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={c.company_ph} />
                      </div>
                      <div>
                        <label className="overline block mb-2">{c.phone}</label>
                        <input data-testid="finder-form-phone" className="field" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={c.phone_ph} />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <button type="button" onClick={back} data-testid="finder-back-result" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
                          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                          {c.adjust}
                        </button>
                        <button type="button" onClick={printPDF} data-testid="finder-print-pdf" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-white/15 hover:border-white/40 px-4 py-2 transition-colors">
                          <Printer className="w-4 h-4" strokeWidth={1.5} />
                          {c.save_pdf}
                        </button>
                      </div>
                      <button type="submit" disabled={submitting} data-testid="finder-submit" className="group inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-4 text-sm font-semibold tracking-tight hover:bg-zinc-200 transition-colors duration-300 disabled:opacity-60">
                        {submitting ? c.sending : c.submit}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {showResult && success && (
                <div data-testid="finder-success" className="flex-1 flex flex-col justify-center items-start gap-6">
                  <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={1.4} />
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight mb-3">{c.success_title}</h3>
                    <p className="text-zinc-400 max-w-md leading-relaxed">
                      {c.success_body_1}{" "}
                      <span className="text-white">{trVerfahren(recommendation.primary)}</span>{" "}
                      {c.success_body_2}
                    </p>
                  </div>
                  <button type="button" onClick={restart} data-testid="finder-restart" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-sm font-semibold hover:bg-white/5 transition-colors">
                    {c.restart}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerfahrenFinder;
